import React, { Fragment, useState } from "react"

import Sub from "~layout/sub"
import aside from "~content/products/aside"

import "../../css/ChineseEbooks.css"

const crumbs = [
  { text: "Products", to: "/products" },
  { text: "Dudu Chinese", to: "/products/dudu/" },
  { text: "Chinese E-Books" },
]

const highlightCards = [
  {
    title: "Library Size and Variety of Topics",
    description:
      "Dudu's expansive library includes over 1,000 interactive Chinese eBooks, catering to learners from preschool through to secondary school levels. The collection spans 13 difficulty levels, each corresponding to approximately one year of learning experience. Topics range from traditional tales and cultural stories to modern narratives, allowing learners to enjoy a riveting and comprehensive journey. Regular additions keep the content fresh and aligned with educational needs.",
  },
  {
    title: "Interactive Features in Each Book",
    description:
      "Each eBook is equipped with interactive elements designed to enhance the learning experience. Features include audio narration, clickable pinyin, and vocabulary highlights, allowing learners to engage actively with the text. These interactive tools support language acquisition by reinforcing pronunciation, comprehension, and retention in an enjoyable manner.",
  },
]

const readingLevels = [
  {
    title: "Preschool",
    description:
      "Introduce young learners to Mandarin through simple stories, bright illustrations, and engaging characters. Content at this stage focuses on building curiosity, early vocabulary, and listening comprehension in a fun and interactive way.",
  },
  {
    title: "Primary School (Levels 3-8)",
    description:
      "Designed to support children's growing language skills, these stories and activities cover familiar themes, cultural tales, and everyday scenarios. Learners expand their vocabulary and reading fluency whilst building confidence in expressing themselves in Mandarin.",
  },
  {
    title: "Primary 1-3",
    description:
      "At this level, learners encounter more structured stories with richer plots. Vocabulary and sentence structures become more varied, supporting foundational reading and comprehension aligned with HSK 2-3 levels. Cultural stories and imaginative tales keep learning engaging and relatable.",
  },
  {
    title: "Primary 4-6",
    description:
      "Learners tackle more complex texts that introduce abstract ideas, emotions, and values. With stories that encourage critical thinking and deeper understanding, this stage builds strong reading comprehension and prepares students for higher-level language use.",
  },
  {
    title: "Secondary 1-4",
    description:
      "Secondary school Chinese books emphasise real-world application of Mandarin with thought-provoking texts, contemporary themes, and advanced vocabulary. Learners refine their reading and writing skills, strengthen cultural awareness, and prepare for examinations and practical communication.",
  },
]

const readingHours = [
  {
    title: "Preschool (0-75 hours)",
    description:
      "Designed for early years or HSK 1 learners, this level introduces foundational vocabulary, simple sentence structures, audio narrations and pinyin support, perfect for laying the groundwork in Chinese reading habits.",
  },
  {
    title: "Primary 1-3 (80-225 hours)",
    description:
      "At this stage, learners transition into more complex narratives and expand their vocabulary through stories aligned with HSK 2 to 3/4. Interactive quizzes and familiar themes help reinforce comprehension and develop confidence.",
  },
  {
    title: "Primary 4-6 (230-600 hours)",
    description:
      "Here, texts challenge readers with richer grammar and broader word usage (HSK 4-6), supported by animations, dialogues and engaging storylines that encourage deeper understanding and critical thinking.",
  },
  {
    title: "Secondary 1-4 (600-1,200 hours)",
    description:
      "Tailored for secondary-level learners, these materials offer dense vocabulary and nuanced content designed to stretch comprehension, reading fluency and language maturity over extended exposure.",
  },
]

const faqSections = [
  {
    title: "What is the DuDu Chinese eBook App and how does it help learners?",
    description:
      "The DuDu Chinese eBook App is a self-paced, adaptive reading platform for learners aged six and above. It recommends interactive Chinese ebooks matched to your reading proficiency across thirteen levels, helping you progress comfortably and confidently. It combines vibrant illustrations, audio narration, pinyin support, quizzes, games and progress tracking to make learning more engaging and effective. ",
  },
  {
    title: "How many levels and books are available in the app?",
    description:
      "DuDu offers books across thirteen carefully structured levels, each roughly equivalent to a year’s learning experience. The library includes hundreds (and growing) of beautifully illustrated and interactive Chinese ebooks, with new titles regularly added so learners always have fresh material to explore. ",
  },
  {
    title: "Does DuDu include features like audio, pinyin or quizzes?",
    description:
      <Fragment>
        <p>Absolutely. Each ebook in the DuDu Chinese eBook App comes with a suite of learning aids:</p>
        <ul>
          <li>Audio narration, aiding listening skills and pronunciation</li>
          <li>Pinyin support, making it easier to read unfamiliar characters</li>
          <li>Interactive quizzes and games to reinforce vocabulary and comprehension. These tools help promote an immersive and effective Chinese reading experience.</li>
        </ul>
      </Fragment>,
  },
  {
    title: "How does the app personalise content to my child's level?",
    description:
      "On first use, learners undertake a placement test to determine their reading sweet spot. The app then uses adaptive technology and research-backed algorithms, such as support vector machines and item response theory, to recommend ebooks aligned with the learner’s current ability, helping to build vocabulary and reading confidence.",
  },
  {
    title: "What progress-tracking tools are included?",
    description:
      "DuDu provides rich analytics for both learners and carers. For students, it tracks reading history, vocabulary lists, and rewards earned. Teachers and parents enjoy mobile-friendly dashboards summarising progress, performance in quizzes and games, and reading consistency. This level of insight helps everyone support the learner’s journey. ",
  },
  {
    title: "Who is DuDu suitable for, children, parents or teachers?",
    description:
      <Fragment>
        <p>DuDu is designed for learners aged six and up, but benefits extend beyond them:</p>
        <ul>
          <li>Children enjoy the engaging content, audio, interactive quizzes and games</li>
          <li>Parents appreciate progress tracking, learning analytics and the ability to monitor reading history</li>
          <li>Teachers value tools like the placement test simulator, ability to assign books, and mobile reports to manage classes</li>
        </ul>
        <p>Together, these features make the DuDu Chinese eBook App a valuable tool for blended learning environments.</p>
      </Fragment>
  },
]

const ChineseEbooksPage = () => {
  const getAccordionId = title =>
    `ebooks-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`

  const [openItems, setOpenItems] = useState(() => [
    getAccordionId(readingLevels[0].title),
    getAccordionId(faqSections[0].title),
  ])

  const toggleItem = id => {
    setOpenItems(current =>
      current.includes(id)
        ? current.filter(itemId => itemId !== id)
        : [...current, id]
    )
  }

  const renderAccordion = items => (
    <div className="ebooks-accordion">
      {items.map(item => {
        const itemId = getAccordionId(item.title)
        const isOpen = openItems.includes(itemId)
        const panelId = `${itemId}-panel`

        return (
          <div className={`ebooks-accordion-item${isOpen ? " is-open" : ""}`} key={itemId}>
            <button
              type="button"
              className="ebooks-accordion-toggle"
              onClick={() => toggleItem(itemId)}
              aria-expanded={isOpen}
              aria-controls={panelId}
            >
              <h3>{item.title}</h3>
              <span className="ebooks-accordion-symbol" aria-hidden="true" />
            </button>

            <div id={panelId} className={`ebooks-accordion-panel${isOpen ? " is-open" : ""}`}>
              <p>{item.description}</p>
            </div>
          </div>
        )
      })}
    </div>
  )

  const content = (
    <Fragment>
      <div className="ebooks-page">
        <h1>Introducing Dudu&apos;s Chinese eBook App</h1>
        <p className="ebooks-intro">
          DuDu&apos;s Chinese eBook library offers an engaging and adaptive reading platform for children aged six and above, with over 800 interactive eBooks across 13 levels. Our Chinese eBook app personalises each child&apos;s learning journey with a placement test, audio narration, Hanyu Pinyin support, quizzes, and educational games, making reading both fun and effective whilst building vocabulary, comprehension, and an appreciation of Chinese culture.
        </p>

        <h2>Enjoy a Wide Range of Chinese Story Books Online</h2>
        <div className="ebooks-highlight-grid">
          {highlightCards.map(card => (
            <div className="ebooks-highlight-card" key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </div>
          ))}
        </div>

        <h2>Chinese Story Books by Reading Level</h2>
        <p className="ebooks-copy">
          To give learners and educators a clearer sense of pace, here&apos;s a breakdown of the typical hours of study aligned with each educational stage:
        </p>
        {renderAccordion(readingLevels)}

        <h2>Reading Chinese Books Online: Recommended Hours per Level</h2>
        <p className="ebooks-copy">
          To give learners and educators a clearer sense of pace, here&apos;s a breakdown of the typical hours of study aligned with each educational stage:
        </p>
        {renderAccordion(readingHours)}

        <div className="ebooks-cta">
          <h2>Your Journey to Reading Chinese Books Online Starts Here</h2>
          <a href="https://reg.dudu.town/cos/o.x?c=/ca4_dd/ddreg&ptid=3028381" title="Sign Up Now" className="ebooks-cta-button" target="_blank" rel="noreferrer">
            Sign Up Now
          </a>
        </div>

        <h2>Frequently Asked Questions</h2>
        {renderAccordion(faqSections)}
      </div>
    </Fragment>
  )

  return <Sub title="Dudu Chinese eBooks" seo_name="Dudu Chinese eBooks" crumbs={crumbs} content={content} aside={aside} />
}

export default ChineseEbooksPage
